import { cn } from "@chams-pay/design-system/lib/utils";
import { useLayoutEffect, useState, type RefObject } from "react";
import { createPortal } from "react-dom";

export function CalendarPopover({
  open,
  anchorRef,
  panelRef,
  align,
  className,
  children,
}: {
  readonly open: boolean;
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly panelRef: RefObject<HTMLDivElement | null>;
  readonly align: "start" | "end";
  readonly className?: string;
  readonly children: React.ReactNode;
}) {
  const [position, setPosition] = useState({ top: 0, left: 0, ready: false });

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const panelWidth = panelRef.current?.offsetWidth ?? 320;
      const panelHeight = panelRef.current?.offsetHeight ?? 360;
      const desiredLeft = align === "end" ? rect.right - panelWidth : rect.left;
      const left = Math.max(8, Math.min(window.innerWidth - panelWidth - 8, desiredLeft));
      const below = rect.bottom + 8;
      const top =
        below + panelHeight <= window.innerHeight ? below : Math.max(8, rect.top - panelHeight - 8);
      setPosition({ top, left, ready: true });
    };
    update();
    const frame = requestAnimationFrame(update);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => update());
    if (panelRef.current) observer?.observe(panelRef.current);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [align, anchorRef, open, panelRef]);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={panelRef}
      style={{ top: position.top, left: position.left }}
      className={cn("fixed z-[100] opacity-0 data-[ready=true]:opacity-100", className)}
      data-ready={position.ready}
      data-calendar-popover
    >
      {children}
    </div>,
    document.body,
  );
}
